import "animate.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();

  const sendResetOTP = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("http://localhost:3001/api/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || "Failed to send reset OTP");
        return;
      }

      toast.success("Password reset OTP sent to your email!");
      setStep(2);
    } catch (err) {
      toast.error("Failed to send reset OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyResetOTP = async () => {
    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("http://localhost:3001/api/verify-reset-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || "Invalid OTP");
        return;
      }

      toast.success("OTP verified! Please set your new password.");
      setStep(3);
    } catch (err) {
      toast.error("Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("http://localhost:3001/api/reset-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, otp, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || "Failed to reset password");
        return;
      }

      toast.success("Password reset successfully! Please login with your new password.");
      setTimeout(() => {
        navigate("/signin");
      }, 2000);
    } catch (err) {
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    setResendLoading(true);
    try {
      const response = await fetch("http://localhost:3001/api/resend-reset-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || "Failed to resend OTP");
        return;
      }

      toast.success("OTP resent to your email!");
      setOtp("");
    } catch (err) {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === 1) {
      sendResetOTP();
    } else if (step === 2) {
      verifyResetOTP();
    } else if (step === 3) {
      resetPassword();
    }
  };

  return (
    <div className="container-fluid vh-100">
      <div className="row h-100">
        <div className="col-md-6 d-none d-md-flex align-items-center justify-content-center">
          <img
            src="/slider/2.jpeg"
            alt="Aarnayours"
            className="img-fluid animate__animated animate__fadeInLeft"
            style={{
              maxHeight: "80%",
              borderRadius: "24px",
            }}
          />
        </div>

        <div className="col-md-6 d-flex align-items-center justify-content-center">
          <div
            className="w-100 animate__animated animate__fadeInUp"
            style={{ maxWidth: "420px" }}
          >
            {/* Header */}
            <div className="text-center mb-4">
              <h2 className="fw-bold mb-2">Reset Password</h2>
              <p className="text-muted">
                {step === 1 && "Enter your email to receive a reset code"}
                {step === 2 && "Enter the OTP sent to your email"}
                {step === 3 && "Create your new password"}
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 1 ? 'bg-primary text-white' : 'bg-light text-muted'}`} style={{ width: '30px', height: '30px', fontSize: '12px' }}>
                  1
                </div>
                <div className={`flex-grow-1 mx-2 ${step >= 2 ? 'bg-primary' : 'bg-light'}`} style={{ height: '2px' }}></div>
                <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 2 ? 'bg-primary text-white' : 'bg-light text-muted'}`} style={{ width: '30px', height: '30px', fontSize: '12px' }}>
                  2
                </div>
                <div className={`flex-grow-1 mx-2 ${step >= 3 ? 'bg-primary' : 'bg-light'}`} style={{ height: '2px' }}></div>
                <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 3 ? 'bg-primary text-white' : 'bg-light text-muted'}`} style={{ width: '30px', height: '30px', fontSize: '12px' }}>
                  3
                </div>
              </div>
              <div className="d-flex justify-content-between mt-2">
                <small className="text-muted">Email</small>
                <small className="text-muted">Verify</small>
                <small className="text-muted">Reset</small>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Email Input */}
              {step === 1 && (
                <>
                  <input
                    type="email"
                    className="form-control mb-3 rounded-pill"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    className="btn btn-primary w-100 rounded-pill mb-3"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send Reset Code"}
                  </button>
                </>
              )}

              {/* Step 2: OTP Verification */}
              {step === 2 && (
                <>
                  <div className="alert alert-info rounded-pill text-center mb-3">
                    <small>Reset code sent to {email}</small>
                  </div>
                  
                  <input
                    type="text"
                    className="form-control mb-3 rounded-pill text-center"
                    placeholder="Enter 4-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    maxLength="4"
                    style={{ letterSpacing: "0.5em", fontSize: "1.2em" }}
                    required
                  />
                  
                  <button
                    type="submit"
                    className="btn btn-success w-100 rounded-pill mb-2"
                    disabled={loading || otp.length !== 4}
                  >
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>
                  
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 rounded-pill mb-3"
                    onClick={resendOTP}
                    disabled={resendLoading}
                  >
                    {resendLoading ? "Resending..." : "Resend OTP"}
                  </button>
                </>
              )}

              {/* Step 3: New Password */}
              {step === 3 && (
                <>
                  <input
                    type="password"
                    className="form-control mb-3 rounded-pill"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  
                  <input
                    type="password"
                    className="form-control mb-3 rounded-pill"
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  
                  <div className="mb-3">
                    <small className="text-muted">
                      Password must be at least 6 characters long
                    </small>
                  </div>
                  
                  <button
                    type="submit"
                    className="btn btn-success w-100 rounded-pill mb-3"
                    disabled={loading}
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </button>
                </>
              )}
            </form>

            {/* Back to Login */}
            <div className="text-center">
              <button
                className="btn btn-link text-decoration-none"
                onClick={() => navigate("/signin")}
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;